import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BlockIcon from '@mui/icons-material/Block';
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import paths from '@src/paths';
import { useMessage } from '@src/context/common';
import apiClient from '@src/api/clients';
import {
  bulkGenerateInvoices,
  deleteInvoice,
  downloadInvoicePdf,
  listBulkGenerationClients,
  listInvoices,
  sendInvoice,
  voidInvoice,
} from '@src/api/invoices';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'outstanding', label: 'Outstanding / Overdue' },
  { value: 'void', label: 'Void' },
  { value: 'paid', label: 'Paid' },
];

const PAYMENT_METHOD_FILTER_OPTIONS = [
  { value: 'all', label: 'All methods' },
  { value: 'card', label: 'Card' },
  { value: 'us_bank_account', label: 'ACH' },
];

const PAYMENT_STATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All payment states' },
  { value: 'paid', label: 'Paid' },
  { value: 'processing', label: 'Processing' },
  { value: 'failed', label: 'Failed' },
  { value: 'none', label: 'None' },
];

const DEFAULT_PAGE_SIZE = 25;
const ANALYTICS_PAGE_SIZE = 200;
const MAX_ANALYTICS_PAGES = 20;
const DEFAULT_SORT = { field: 'issue_date', direction: 'desc' };
const PERIOD_WINDOW_OPTIONS = [
  { value: '3', label: 'Last 3 periods' },
  { value: '6', label: 'Last 6 periods' },
  { value: '12', label: 'Last 12 periods' },
  { value: 'all', label: 'All periods' },
];

const getDefaultPeriod = () => {
  const end = dayjs().subtract(1, 'month').endOf('month');
  const start = end.startOf('month');
  return {
    start: start.format('YYYY-MM-DD'),
    end: end.format('YYYY-MM-DD'),
  };
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return String(value);
  }
  return amount.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const formatSpendFundingLabel = (value) => {
  if (!value) {
    return '—';
  }
  if (value === 'client_funds_spend') {
    return 'Client funded';
  }
  if (value === 'guru_funds_spend') {
    return 'Lead Gurus funded';
  }
  return value;
};

const formatPaymentSourceLabel = (value) => {
  if (!value) {
    return '—';
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'stripe') {
    return 'Stripe';
  }
  if (normalized === 'manual') {
    return 'Manual';
  }
  return normalized;
};

const formatPaymentMethodLabel = (value) => {
  if (!value) {
    return '—';
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'ach_wire') {
    return 'ACH/Wire';
  }
  if (normalized === 'us_bank_account') {
    return 'ACH';
  }
  if (normalized === 'check') {
    return 'Check';
  }
  if (normalized === 'card') {
    return 'Card';
  }
  return normalized.replace(/_/g, ' ');
};

const getPaymentState = (invoice) => {
  const paymentState = String(invoice?.payment_state || '').toLowerCase();
  if (!paymentState) {
    if (String(invoice?.status || '').toLowerCase() === 'paid') {
      return { label: 'Paid', color: 'success' };
    }
    if (invoice?.payment_failed_at) {
      return { label: 'Failed', color: 'error' };
    }
    if (String(invoice?.payment_source || '').toLowerCase() === 'stripe') {
      return { label: 'Processing', color: 'info' };
    }
  }
  if (paymentState === 'paid') {
    return { label: 'Paid', color: 'success' };
  }
  if (paymentState === 'failed') {
    return { label: 'Failed', color: 'error' };
  }
  if (paymentState === 'processing') {
    return { label: 'Processing', color: 'info' };
  }
  return { label: '—', color: 'default' };
};

const statusColor = {
  draft: 'default',
  sent: 'info',
  outstanding: 'warning',
  void: 'default',
  paid: 'success',
};

const resolveStatusColor = (status) => statusColor[status] || 'default';

const getStatusPresentation = (invoice) => {
  const status = String(invoice?.status || '').toLowerCase();
  const dueDate = invoice?.due_date;
  const overdue =
    status === 'outstanding' &&
    dueDate &&
    dayjs(dueDate).isValid() &&
    dayjs(dueDate).isBefore(dayjs().startOf('day'));

  if (overdue) {
    return { label: 'OVERDUE', color: 'error' };
  }
  return {
    label: status ? status.toUpperCase() : 'UNKNOWN',
    color: resolveStatusColor(status),
  };
};

const getApprovalChipProps = (needsAmountConfirmation) => ({
  label: needsAmountConfirmation ? 'Needs review' : 'Approved',
  color: needsAmountConfirmation ? 'warning' : 'success',
  variant: needsAmountConfirmation ? 'outlined' : 'filled',
});

function mapInvoicesResponse(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length };
  }
  if (data && Array.isArray(data.results)) {
    return { results: data.results, count: data.count ?? data.results.length };
  }
  return { results: [], count: 0 };
}

const toNumber = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

const getPeriodKey = (invoice) =>
  `${invoice?.period_start || 'unknown'}__${invoice?.period_end || 'unknown'}`;

const formatPeriodLabel = (invoiceOrSummary) =>
  `${invoiceOrSummary?.period_start || '—'} – ${invoiceOrSummary?.period_end || '—'}`;

const getPeriodDisplayLabel = (periodStart, periodEnd) => {
  const start = dayjs(periodStart);
  const end = dayjs(periodEnd);
  if (!start.isValid() || !end.isValid()) {
    return `${periodStart || '—'} – ${periodEnd || '—'}`;
  }

  const sameMonth = start.year() === end.year() && start.month() === end.month();
  const monthLabel = start.format('MMM YYYY');
  const endOfMonth = start.endOf('month').date();

  if (sameMonth && start.date() === 1 && end.date() === 15) {
    return `${monthLabel} P1`;
  }
  if (sameMonth && start.date() === 16 && end.date() === endOfMonth) {
    return `${monthLabel} P2`;
  }
  if (sameMonth && start.date() === 1 && end.date() === endOfMonth) {
    return monthLabel;
  }

  if (start.year() === end.year()) {
    return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
  }
  return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
};

const compareInvoiceValues = (left, right, { field, direction }) => {
  const multiplier = direction === 'desc' ? -1 : 1;

  if (field === 'client_sort_name') {
    const leftValue = String(left?.client_name || left?.client || '').toLowerCase();
    const rightValue = String(right?.client_name || right?.client || '').toLowerCase();
    return leftValue.localeCompare(rightValue) * multiplier;
  }

  if (field === 'total_amount') {
    return (toNumber(left?.total_amount) - toNumber(right?.total_amount)) * multiplier;
  }

  if (field === 'issue_date') {
    const leftValue = dayjs(left?.issue_date).valueOf();
    const rightValue = dayjs(right?.issue_date).valueOf();
    if (leftValue !== rightValue) {
      return (leftValue - rightValue) * multiplier;
    }
  }

  return (Number(left?.id || 0) - Number(right?.id || 0)) * multiplier;
};

const isInvoiceOverdue = (invoice) => {
  const status = String(invoice?.status || '').toLowerCase();
  const dueDate = invoice?.due_date;
  if (status !== 'outstanding' || !dueDate) {
    return false;
  }
  if (!dayjs(dueDate).isValid()) {
    return false;
  }
  return dayjs(dueDate).isBefore(dayjs().startOf('day'));
};

const getInvoiceRevenue = (invoice, { status = 'paid', aging = 'all' } = {}) => {
  const invoiceStatus = String(invoice?.status || '').toLowerCase();
  if (status !== 'all' && invoiceStatus !== status) {
    return 0;
  }
  if (status === 'outstanding') {
    const overdue = isInvoiceOverdue(invoice);
    if (aging === 'overdue' && !overdue) {
      return 0;
    }
    if (aging === 'open' && overdue) {
      return 0;
    }
  }
  return (invoice?.line_items || []).reduce((sum, item) => {
    const lineItemType = String(item?.line_item_type || '').trim().toLowerCase();
    const description = String(item?.description || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    const isMediaFeeLine =
      lineItemType === 'media_fee' || ['media spend fee', 'media spend fees'].includes(description);
    const isTopUpLine =
      lineItemType === 'monthly_min_top_up' || description === 'monthly minimum fee top-up';
    if (!isMediaFeeLine && !isTopUpLine) {
      return sum;
    }
    return sum + toNumber(item?.line_total ?? item?.amount);
  }, 0);
};

const hasMonthlyTopUp = (invoice) =>
  (invoice?.line_items || []).some((item) => {
    const lineItemType = String(item?.line_item_type || '').trim().toLowerCase();
    const description = String(item?.description || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    return lineItemType === 'monthly_min_top_up' || description === 'monthly minimum fee top-up';
  });

const toCsvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

export default function InvoiceListPage() {
  const message = useMessage();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    search: '',
    amount: '',
    status: 'all',
    funding: 'all',
    approval: 'all',
    paymentMethod: 'all',
    paymentState: 'all',
    paymentDateStart: '',
    paymentDateEnd: '',
    issueStart: '',
    issueEnd: '',
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debouncedAmount, setDebouncedAmount] = useState('');
  const searchTimerRef = useRef(null);
  const amountTimerRef = useRef(null);
  const fetchAbortRef = useRef(null);
  const [pagination, setPagination] = useState({ page: 0, rowsPerPage: DEFAULT_PAGE_SIZE });
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [voidingId, setVoidingId] = useState(null);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');

  const [clients, setClients] = useState([]);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkClients, setBulkClients] = useState([]);
  const [bulkClientsLoading, setBulkClientsLoading] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [bulkFundingFilter, setBulkFundingFilter] = useState('all');
  const [bulkPeriod, setBulkPeriod] = useState(() => getDefaultPeriod());
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [analyticsInvoices, setAnalyticsInvoices] = useState([]);
  const [periodWindow, setPeriodWindow] = useState('6');
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [collapsedPeriods, setCollapsedPeriods] = useState({});

  const buildInvoiceQueryParams = useCallback(
    ({ page, pageSize }) => {
      const amountInput = String(debouncedAmount ?? '').trim();
      const amount = Number(amountInput);
      const tolerance = 3;
      const hasAmount = amountInput !== '' && Number.isFinite(amount);
      const hasTolerance = Number.isFinite(tolerance) && tolerance > 0;
      return {
        page,
        page_size: pageSize,
        search: debouncedSearch,
        total_amount:
          hasAmount && !hasTolerance ? amount : undefined,
        total_amount__gte:
          hasAmount && hasTolerance ? amount - tolerance : undefined,
        total_amount__lte:
          hasAmount && hasTolerance ? amount + tolerance : undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        spend_funding: filters.funding !== 'all' ? filters.funding : undefined,
        needs_amount_confirmation:
          filters.approval === 'all'
            ? undefined
            : filters.approval === 'approved'
              ? false
              : true,
        payment_method:
          filters.paymentMethod !== 'all' ? filters.paymentMethod : undefined,
        payment_state:
          filters.paymentState !== 'all' ? filters.paymentState : undefined,
        payment_date__gte: filters.paymentDateStart || undefined,
        payment_date__lte: filters.paymentDateEnd || undefined,
        issue_date__gte: filters.issueStart || undefined,
        issue_date__lte: filters.issueEnd || undefined,
        ordering: `${sort.direction === 'desc' ? '-' : ''}${sort.field}`,
      };
    },
    [debouncedSearch, debouncedAmount, filters.status, filters.funding, filters.approval, filters.paymentMethod, filters.paymentState, filters.paymentDateStart, filters.paymentDateEnd, filters.issueStart, filters.issueEnd, sort]
  );

  const clientFundingById = useMemo(() => {
    const map = new Map();
    clients.forEach((client) => {
      if (client?.id) {
        map.set(client.id, client.spendFunding || '');
      }
    });
    return map;
  }, [clients]);

  const filteredClients = useMemo(() => {
    return bulkClients;
  }, [bulkClients]);

  const loadClients = useCallback(() => {
    apiClient
      .request({ url: 'dashboard/clients/', method: 'GET' })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setClients(
          data
            .map((client) => ({
              id: client.id,
              name: client.name,
              spendFunding: client.spend_funding,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch((err) => {
        console.error('Failed to load clients', err);
        message.error('Unable to load clients for bulk generation.');
      });
  }, [message]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (!bulkDialogOpen) {
      return;
    }
    if ((bulkPeriod.start && !bulkPeriod.end) || (!bulkPeriod.start && bulkPeriod.end)) {
      setBulkClientsLoading(false);
      setBulkClients([]);
      return;
    }
    setBulkError('');
    setBulkClientsLoading(true);
    listBulkGenerationClients({
      period_start: bulkPeriod.start || undefined,
      period_end: bulkPeriod.end || undefined,
      spend_funding: bulkFundingFilter !== 'all' ? bulkFundingFilter : undefined,
    })
      .then((res) => {
        const data = Array.isArray(res.data?.results) ? res.data.results : [];
        setBulkClients(
          data.map((client) => ({
            id: client.id,
            name: client.name,
            spendFunding: client.spend_funding,
            periodSpend: client.period_spend,
          }))
        );
      })
      .catch((err) => {
        console.error('Failed to load spend-eligible clients', err);
        setBulkClients([]);
        setBulkError('Unable to load clients with spend for the selected period.');
      })
      .finally(() => setBulkClientsLoading(false));
  }, [bulkDialogOpen, bulkFundingFilter, bulkPeriod.end, bulkPeriod.start]);

  useEffect(() => {
    const validIds = new Set(filteredClients.map((client) => client.id));
    setSelectedClientIds((prev) => prev.filter((id) => validIds.has(Number(id))));
  }, [filteredClients]);

  const fetchInvoices = useCallback(() => {
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoading(true);
    setError('');
    const params = buildInvoiceQueryParams({
      page: pagination.page + 1,
      pageSize: pagination.rowsPerPage,
    });
    listInvoices(params, { signal: controller.signal })
      .then((res) => {
        const mapped = mapInvoicesResponse(res.data);
        setInvoices(mapped.results);
        setTotalCount(mapped.count);
      })
      .catch((err) => {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        console.error('Failed to fetch invoices', err);
        setError('Failed to fetch invoices. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [buildInvoiceQueryParams, pagination.page, pagination.rowsPerPage]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const fetchInvoiceAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      let page = 1;
      let totalCount = 0;
      const all = [];
      while (page <= MAX_ANALYTICS_PAGES) {
        const res = await listInvoices(
          buildInvoiceQueryParams({ page, pageSize: ANALYTICS_PAGE_SIZE })
        );
        const mapped = mapInvoicesResponse(res.data);
        totalCount = mapped.count || totalCount;
        all.push(...mapped.results);
        if (!mapped.results.length || all.length >= totalCount) {
          break;
        }
        page += 1;
      }
      setAnalyticsInvoices(all);
    } catch (err) {
      console.error('Failed to fetch invoice analytics', err);
      setAnalyticsInvoices([]);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [buildInvoiceQueryParams]);

  useEffect(() => {
    fetchInvoiceAnalytics();
  }, [fetchInvoiceAnalytics]);

  useEffect(() => {
    const source = analyticsInvoices.length ? analyticsInvoices : invoices;
    if (!source.length) {
      setSelectedInvoiceIds([]);
      return;
    }
    const validIds = new Set(source.map((invoice) => String(invoice.id)));
    setSelectedInvoiceIds((prev) => prev.filter((id) => validIds.has(String(id))));
  }, [analyticsInvoices, invoices]);

  const periodSummaries = useMemo(() => {
    const source = analyticsInvoices.length ? analyticsInvoices : invoices;
    const map = new Map();
    source.forEach((invoice) => {
      const key = getPeriodKey(invoice);
      if (!map.has(key)) {
        map.set(key, {
          period_start: invoice.period_start,
          period_end: invoice.period_end,
          total_billed: 0,
          total_paid: 0,
          total_outstanding: 0,
          total_overdue: 0,
          total_sent: 0,
          total_draft: 0,
          paid_revenue: 0,
          outstanding_revenue: 0,
          overdue_revenue: 0,
        });
      }
      const summary = map.get(key);
      const amount = toNumber(invoice.total_amount);
      summary.total_billed += amount;
      summary.paid_revenue += getInvoiceRevenue(invoice, { status: 'paid' });
      summary.outstanding_revenue += getInvoiceRevenue(invoice, { status: 'outstanding', aging: 'open' });
      summary.overdue_revenue += getInvoiceRevenue(invoice, { status: 'outstanding', aging: 'overdue' });
      if (invoice.status === 'paid') {
        summary.total_paid += amount;
      } else if (invoice.status === 'outstanding') {
        if (isInvoiceOverdue(invoice)) {
          summary.total_overdue += amount;
        } else {
          summary.total_outstanding += amount;
        }
      } else if (invoice.status === 'sent') {
        summary.total_sent += amount;
      } else if (invoice.status === 'draft') {
        summary.total_draft += amount;
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      String(a.period_start || '').localeCompare(String(b.period_start || ''))
    );
  }, [analyticsInvoices, invoices]);

  const periodSummaryByKey = useMemo(() => {
    const map = new Map();
    periodSummaries.forEach((summary) => {
      map.set(`${summary.period_start || 'unknown'}__${summary.period_end || 'unknown'}`, summary);
    });
    return map;
  }, [periodSummaries]);

  const selectedInvoiceIdSet = useMemo(
    () => new Set(selectedInvoiceIds.map((id) => String(id))),
    [selectedInvoiceIds]
  );

  const allVisibleSelected =
    invoices.length > 0 && invoices.every((invoice) => selectedInvoiceIdSet.has(String(invoice.id)));
  const someVisibleSelected =
    invoices.some((invoice) => selectedInvoiceIdSet.has(String(invoice.id))) && !allVisibleSelected;

  const filteredPeriodSummaries = useMemo(() => {
    if (periodWindow === 'all') {
      return periodSummaries;
    }
    const count = Number(periodWindow);
    if (!Number.isFinite(count) || count <= 0) {
      return periodSummaries;
    }
    return periodSummaries.slice(-count);
  }, [periodSummaries, periodWindow]);

  const invoicePeriodGroups = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    invoices.forEach((invoice) => {
      const key = getPeriodKey(invoice);
      if (!byKey.has(key)) {
        const group = {
          key,
          periodStart: invoice.period_start,
          periodEnd: invoice.period_end,
          invoices: [],
        };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).invoices.push(invoice);
    });
    groups.sort((a, b) => {
      const left = dayjs(a.periodStart);
      const right = dayjs(b.periodStart);
      const leftValue = left.isValid() ? left.valueOf() : 0;
      const rightValue = right.isValid() ? right.valueOf() : 0;
      return rightValue - leftValue;
    });
    groups.forEach((group) => {
      group.invoices.sort((a, b) => compareInvoiceValues(a, b, sort));
    });
    return groups;
  }, [invoices, sort]);

  useEffect(() => {
    if (!invoicePeriodGroups.length) {
      return;
    }
    setCollapsedPeriods((prev) => {
      const next = { ...prev };
      invoicePeriodGroups.forEach((group) => {
        if (next[group.key] === undefined) {
          next[group.key] = false;
        }
      });
      return next;
    });
  }, [invoicePeriodGroups]);

  const totals = useMemo(
    () =>
      filteredPeriodSummaries.reduce(
        (acc, row) => ({
          total_billed: acc.total_billed + row.total_billed,
          total_paid: acc.total_paid + row.total_paid,
          total_outstanding: acc.total_outstanding + row.total_outstanding,
          total_overdue: acc.total_overdue + row.total_overdue,
          paid_revenue: acc.paid_revenue + row.paid_revenue,
          outstanding_revenue: acc.outstanding_revenue + row.outstanding_revenue,
          overdue_revenue: acc.overdue_revenue + row.overdue_revenue,
        }),
        {
          total_billed: 0,
          total_paid: 0,
          total_outstanding: 0,
          total_overdue: 0,
          paid_revenue: 0,
          outstanding_revenue: 0,
          overdue_revenue: 0,
        }
      ),
    [filteredPeriodSummaries]
  );

  const chartData = useMemo(
    () =>
      filteredPeriodSummaries.map((row) => ({
        period: getPeriodDisplayLabel(row.period_start, row.period_end),
        billed: Number(row.total_billed.toFixed(2)),
        paid: Number(row.total_paid.toFixed(2)),
        outstanding: Number(row.total_outstanding.toFixed(2)),
        overdue: Number(row.total_overdue.toFixed(2)),
        paid_revenue: Number(row.paid_revenue.toFixed(2)),
        outstanding_revenue: Number(row.outstanding_revenue.toFixed(2)),
        overdue_revenue: Number(row.overdue_revenue.toFixed(2)),
      })),
    [filteredPeriodSummaries]
  );

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (key === 'search') {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setDebouncedSearch(value);
        setPagination((prev) => ({ ...prev, page: 0 }));
      }, 400);
    } else if (key === 'amount') {
      clearTimeout(amountTimerRef.current);
      amountTimerRef.current = setTimeout(() => {
        setDebouncedAmount(value);
        setPagination((prev) => ({ ...prev, page: 0 }));
      }, 400);
    } else {
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const handleRequestSort = (field) => {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { field, direction: 'asc' };
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleToggleInvoiceSelection = (invoiceId) => {
    const key = String(invoiceId);
    setSelectedInvoiceIds((prev) => {
      if (prev.some((id) => String(id) === key)) {
        return prev.filter((id) => String(id) !== key);
      }
      return [...prev, invoiceId];
    });
  };

  const togglePeriodCollapse = (periodKey) => {
    setCollapsedPeriods((prev) => ({ ...prev, [periodKey]: !prev[periodKey] }));
  };

  const handleToggleSelectVisible = (event) => {
    const shouldSelect = Boolean(event.target.checked);
    const visibleIds = invoices.map((invoice) => invoice.id);
    setSelectedInvoiceIds((prev) => {
      const prevSet = new Set(prev.map((id) => String(id)));
      if (shouldSelect) {
        visibleIds.forEach((id) => prevSet.add(String(id)));
        return Array.from(prevSet);
      }
      visibleIds.forEach((id) => prevSet.delete(String(id)));
      return Array.from(prevSet);
    });
  };

  const handleQuickSend = (invoiceId) => {
    setSendingId(invoiceId);
    sendInvoice(invoiceId, {})
      .then((res) => {
        message.success('Invoice sent successfully. Stripe payment link created.');
        setInvoices((prev) => prev.map((invoice) => (invoice.id === invoiceId ? res.data : invoice)));
      })
      .catch((err) => {
        console.error('Failed to send invoice', err);
        message.error('Failed to send the invoice. Please review invoice details.');
      })
      .finally(() => setSendingId(null));
  };

  const handleDownloadPdf = useCallback(
    (invoice) => {
      if (!invoice?.id) {
        return;
      }
      setDownloadingId(invoice.id);
      downloadInvoicePdf(invoice.id)
        .then((blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          const filename = `${invoice.number || `invoice-${invoice.id}`}.pdf`;
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch((err) => {
          console.error('Failed to download invoice PDF', err);
          message.error('Unable to download invoice PDF.');
        })
        .finally(() => setDownloadingId(null));
    },
    [message]
  );

  const openDeleteDialog = (invoice) => {
    if (!invoice || invoice.status !== 'draft') {
      return;
    }
    setDeleteTarget(invoice);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingId) {
      return;
    }
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const openVoidDialog = (invoice) => {
    if (!invoice || ['paid', 'void'].includes(invoice.status) || getPaymentState(invoice).label === 'Processing') {
      return;
    }
    setVoidTarget(invoice);
    setVoidReason('');
    setVoidDialogOpen(true);
  };

  const closeVoidDialog = () => {
    if (voidingId) {
      return;
    }
    setVoidDialogOpen(false);
    setVoidTarget(null);
    setVoidReason('');
  };

  const handleDelete = () => {
    if (!deleteTarget?.id) {
      return;
    }
    setDeletingId(deleteTarget.id);
    deleteInvoice(deleteTarget.id)
      .then(() => {
        message.success('Draft invoice deleted.');
        closeDeleteDialog();
        fetchInvoices();
      })
      .catch((err) => {
        console.error('Failed to delete invoice', err);
        message.error('Unable to delete this invoice. Please try again.');
      })
      .finally(() => setDeletingId(null));
  };

  const handleVoid = () => {
    if (!voidTarget?.id) {
      return;
    }
    setVoidingId(voidTarget.id);
    voidInvoice(voidTarget.id, { reason: voidReason })
      .then(() => {
        message.success('Invoice voided.');
        closeVoidDialog();
        fetchInvoices();
      })
      .catch((err) => {
        console.error('Failed to void invoice', err);
        message.error(err?.response?.data?.detail || 'Unable to void this invoice. Please try again.');
      })
      .finally(() => setVoidingId(null));
  };

  const openBulkDialog = () => {
    setBulkSummary(null);
    setBulkError('');
    setBulkClients([]);
    setSelectedClientIds([]);
    setBulkPeriod(getDefaultPeriod());
    setBulkFundingFilter('all');
    setBulkDialogOpen(true);
  };

  const closeBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkClients([]);
  };

  const handleExportCsv = () => {
    const source = analyticsInvoices.length ? analyticsInvoices : invoices;
    const rowsToExport = selectedInvoiceIds.length
      ? source.filter((invoice) => selectedInvoiceIdSet.has(String(invoice.id)))
      : source;

    if (!rowsToExport.length) {
      message.error('No invoices available to export for the current selection.');
      return;
    }

    const headers = [
      'Invoice',
      'Client',
      'Funding',
      'Approval',
      'Period',
      'Status',
      'Payment status',
      'Amount paid',
      'Balance due',
      'Total',
      'Revenue (Paid)',
      'Revenue (Outstanding)',
      'Revenue (Overdue)',
      'Payment source',
      'Payment method',
      'Payment state',
      'Payment date',
      'Issue date',
      'Due date',
      'Top-up applied',
    ];

    const lines = [headers.map(toCsvCell).join(',')];
    rowsToExport.forEach((invoice) => {
      const row = [
        invoice.number || `Invoice #${invoice.id}`,
        invoice.client_name || invoice.client || '',
        formatSpendFundingLabel(clientFundingById.get(invoice.client)),
        invoice.needs_amount_confirmation ? 'Needs review' : 'Approved',
        getPeriodDisplayLabel(invoice.period_start, invoice.period_end),
        (invoice.status || '').toUpperCase(),
        (invoice.payment_status || 'unpaid').toUpperCase(),
        toNumber(invoice.amount_paid || 0).toFixed(2),
        toNumber(invoice.balance_due ?? invoice.total_amount).toFixed(2),
        toNumber(invoice.total_amount).toFixed(2),
        getInvoiceRevenue(invoice, { status: 'paid' }).toFixed(2),
        getInvoiceRevenue(invoice, { status: 'outstanding', aging: 'open' }).toFixed(2),
        getInvoiceRevenue(invoice, { status: 'outstanding', aging: 'overdue' }).toFixed(2),
        formatPaymentSourceLabel(invoice.payment_source),
        formatPaymentMethodLabel(invoice.payment_method),
        getPaymentState(invoice).label,
        invoice.paid_at ? dayjs(invoice.paid_at).format('YYYY-MM-DD HH:mm') : '',
        invoice.issue_date || '',
        invoice.due_date || '',
        hasMonthlyTopUp(invoice) ? 'Yes' : 'No',
      ];
      lines.push(row.map(toCsvCell).join(','));
    });

    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = dayjs().format('YYYYMMDD_HHmmss');
    link.href = url;
    link.download = `invoices_${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    message.success(
      `Exported ${rowsToExport.length} invoice${rowsToExport.length === 1 ? '' : 's'} to CSV.`
    );
  };

  const handleSelectAllBulkClients = () => {
    setSelectedClientIds(filteredClients.map((client) => client.id));
  };

  const handleClearBulkClients = () => {
    setSelectedClientIds([]);
  };

  const handleBulkSubmit = () => {
    if (!selectedClientIds.length) {
      setBulkError('Please choose at least one client.');
      return;
    }
    setBulkSubmitting(true);
    setBulkError('');
    setBulkSummary(null);
    const payload = {
      client_ids: selectedClientIds,
      period_start: bulkPeriod.start || undefined,
      period_end: bulkPeriod.end || undefined,
    };
    bulkGenerateInvoices(payload)
      .then((res) => {
        setBulkSummary(res.data);
        message.success('Invoices generated successfully.');
        fetchInvoices();
      })
      .catch((err) => {
        console.error('Failed to bulk generate invoices', err);
        setBulkError('Unable to generate invoices. Please review your selections.');
      })
      .finally(() => setBulkSubmitting(false));
  };

  const bulkSummaryDetails = useMemo(() => {
    if (!bulkSummary) {
      return null;
    }
    const created = bulkSummary.created || [];
    const skipped = bulkSummary.skipped_client_ids || [];
    const skippedExisting = bulkSummary.skipped_existing_client_ids || [];
    const skippedNoSpend = bulkSummary.skipped_no_spend_client_ids || [];
    return (
      <Stack spacing={1} mt={2}>
        <Alert severity="success">
          {created.length ? `${created.length} invoice(s) created.` : 'No new invoices were created.'}
        </Alert>
        {!!created.length && (
          <Paper variant="outlined">
            <Box p={2}>
              <Typography variant="subtitle2" gutterBottom>
                Created Invoices
              </Typography>
              <Stack spacing={0.5}>
                {created.map((invoice) => (
                  <Stack key={invoice.id} direction="row" spacing={1} alignItems="center">
                    <SummarizeIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {invoice.number || `Invoice #${invoice.id}`} — {invoice.client_name} ({invoice.period_start} → {invoice.period_end})
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Paper>
        )}
        {!!skipped.length && (
          <Alert severity="warning">
            Skipped client IDs: {skipped.join(', ')}
          </Alert>
        )}
        {!!skippedExisting.length && (
          <Alert severity="info">
            Existing invoices already covered client IDs: {skippedExisting.join(', ')}
          </Alert>
        )}
        {!!skippedNoSpend.length && (
          <Alert severity="warning">
            No spend in selected period for client IDs: {skippedNoSpend.join(', ')}
          </Alert>
        )}
      </Stack>
    );
  }, [bulkSummary]);

  const renderInvoiceRow = (invoice) => (
    <TableRow key={invoice.id} hover>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selectedInvoiceIdSet.has(String(invoice.id))}
          onChange={() => handleToggleInvoiceSelection(invoice.id)}
          inputProps={{ 'aria-label': `select invoice ${invoice.id}` }}
        />
      </TableCell>
      <TableCell>
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {invoice.number || `Invoice #${invoice.id}`}
          </Typography>
          {hasMonthlyTopUp(invoice) && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              label="Top-up"
              sx={{ width: 'fit-content' }}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{invoice.client_name || invoice.client}</Typography>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={
            clientFundingById.get(invoice.client) === 'client_funds_spend'
              ? 'success.main'
              : 'text.secondary'
          }
          fontWeight={
            clientFundingById.get(invoice.client) === 'client_funds_spend'
              ? 600
              : 400
          }
        >
          {formatSpendFundingLabel(clientFundingById.get(invoice.client))}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip size="small" {...getApprovalChipProps(invoice.needs_amount_confirmation)} />
      </TableCell>
      <TableCell>
        <Stack spacing={0.25}>
          <Typography variant="body2" fontWeight={600}>
            {getPeriodDisplayLabel(invoice.period_start, invoice.period_end)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {invoice.period_start} – {invoice.period_end}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Chip
            label={getStatusPresentation(invoice).label}
            color={getStatusPresentation(invoice).color}
            size="small"
          />
          {invoice.payment_status === 'partial' && (
            <Chip
              label={`Partial (${formatCurrency(invoice.amount_paid)})`}
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>{formatCurrency(invoice.total_amount)}</TableCell>
      <TableCell>{formatCurrency(getInvoiceRevenue(invoice, { status: 'paid' }))}</TableCell>
      <TableCell>{formatCurrency(getInvoiceRevenue(invoice, { status: 'outstanding', aging: 'open' }))}</TableCell>
      <TableCell>{formatPaymentSourceLabel(invoice.payment_source)}</TableCell>
      <TableCell>{formatPaymentMethodLabel(invoice.payment_method)}</TableCell>
      <TableCell>
        <Chip
          label={getPaymentState(invoice).label}
          color={getPaymentState(invoice).color}
          size="small"
        />
      </TableCell>
      <TableCell>{invoice.paid_at ? dayjs(invoice.paid_at).format('YYYY-MM-DD HH:mm') : '—'}</TableCell>
      <TableCell>{invoice.issue_date || '—'}</TableCell>
      <TableCell>{invoice.due_date || '—'}</TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Tooltip title="View details">
            <IconButton
              component={RouterLink}
              to={paths.dashboard.invoices.details(invoice.id)}
              color="primary"
              size="small"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              invoice.status === 'paid' || invoice.status === 'void'
                ? 'Paid or void invoices cannot be edited'
                : 'Edit invoice'
            }
          >
            <span>
              <IconButton
                size="small"
                color="primary"
                disabled={invoice.status === 'paid' || invoice.status === 'void'}
                {...(invoice.status !== 'paid' && invoice.status !== 'void'
                  ? {
                      component: RouterLink,
                      to: paths.dashboard.invoices.edit(invoice.id),
                    }
                  : {})}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download PDF">
            <span>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => handleDownloadPdf(invoice)}
                disabled={downloadingId === invoice.id}
              >
                {downloadingId === invoice.id ? (
                  <CircularProgress size={18} />
                ) : (
                  <DescriptionIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              invoice.needs_amount_confirmation
                ? 'Approve before sending'
                : 'Send invoice'
            }
          >
            <span>
              <IconButton
                color="secondary"
                size="small"
                disabled={
                  invoice.status === 'paid' ||
                  invoice.status === 'void' ||
                  invoice.needs_amount_confirmation ||
                  sendingId === invoice.id
                }
                onClick={() => handleQuickSend(invoice.id)}
              >
                {sendingId === invoice.id ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              invoice.status === 'paid'
              || invoice.status === 'void'
              || getPaymentState(invoice).label === 'Processing'
                ? 'Paid, void, or processing invoices cannot be voided'
                : 'Void invoice'
            }
          >
            <span>
              <IconButton
                size="small"
                color="warning"
                disabled={
                  invoice.status === 'paid' ||
                  invoice.status === 'void' ||
                  getPaymentState(invoice).label === 'Processing' ||
                  voidingId === invoice.id
                }
                onClick={() => openVoidDialog(invoice)}
              >
                {voidingId === invoice.id ? <CircularProgress size={18} /> : <BlockIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              invoice.status === 'draft'
                ? 'Delete draft'
                : 'Only draft invoices can be deleted'
            }
          >
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={invoice.status !== 'draft' || deletingId === invoice.id}
                onClick={() => openDeleteDialog(invoice)}
              >
                {deletingId === invoice.id ? <CircularProgress size={18} /> : <DeleteOutlineIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );

  return (
    <Box p={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} mb={3}>
        <Typography variant="h4">Invoices</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} width={{ xs: '100%', sm: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={!invoices.length && !analyticsInvoices.length}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<SummarizeIcon />}
            onClick={openBulkDialog}
          >
            Bulk generate
          </Button>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate(paths.dashboard.invoices.settings)}
          >
            Invoice settings
          </Button>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            color="primary"
            onClick={() => navigate(paths.dashboard.invoices.create)}
          >
            New invoice
          </Button>
        </Stack>
      </Stack>

      <Card>
        <CardHeader
          title="Filters"
          action={
            <IconButton onClick={fetchInvoices} aria-label="Refresh">
              <RefreshIcon />
            </IconButton>
          }
        />
        <Divider />
        <CardContent>
          <Stack
            spacing={2}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ md: 'center' }}
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              label="Search"
              placeholder="Search by invoice number or client"
              value={filters.search}
              onChange={handleFilterChange('search')}
              fullWidth
              sx={{ minWidth: 240, flex: 1 }}
            />
            <TextField
              label="Amount"
              placeholder="Approx total, e.g. 1250.00 (auto ±$3)"
              value={filters.amount}
              onChange={handleFilterChange('amount')}
              sx={{ minWidth: 180 }}
            />
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={handleFilterChange('status')}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Funding</InputLabel>
              <Select
                label="Funding"
                value={filters.funding}
                onChange={handleFilterChange('funding')}
              >
                <MenuItem value="all">All funding</MenuItem>
                <MenuItem value="guru_funds_spend">Lead Gurus funded</MenuItem>
                <MenuItem value="client_funds_spend">Client funded</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 170 }}>
              <InputLabel>Approval</InputLabel>
              <Select
                label="Approval"
                value={filters.approval}
                onChange={handleFilterChange('approval')}
              >
                <MenuItem value="all">All approvals</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="needs_approval">Needs approval</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 190 }}>
              <InputLabel>Payment method</InputLabel>
              <Select
                label="Payment method"
                value={filters.paymentMethod}
                onChange={handleFilterChange('paymentMethod')}
              >
                {PAYMENT_METHOD_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 190 }}>
              <InputLabel>Payment state</InputLabel>
              <Select
                label="Payment state"
                value={filters.paymentState}
                onChange={handleFilterChange('paymentState')}
              >
                {PAYMENT_STATE_FILTER_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="Payment date from"
              value={filters.paymentDateStart}
              onChange={handleFilterChange('paymentDateStart')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              type="date"
              label="Payment date to"
              value={filters.paymentDateEnd}
              onChange={handleFilterChange('paymentDateEnd')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              type="date"
              label="Issued after"
              value={filters.issueStart}
              onChange={handleFilterChange('issueStart')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              type="date"
              label="Issued before"
              value={filters.issueEnd}
              onChange={handleFilterChange('issueEnd')}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Box mt={3}>
        <Card>
          <CardHeader
            title="Billing Period Performance"
            subheader={
              analyticsLoading
                ? 'Refreshing chart data...'
                : `Showing ${filteredPeriodSummaries.length} of ${periodSummaries.length} periods`
            }
            action={
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Window</InputLabel>
                <Select
                  label="Window"
                  value={periodWindow}
                  onChange={(event) => setPeriodWindow(event.target.value)}
                >
                  {PERIOD_WINDOW_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          />
          <Divider />
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
              <Chip label={`Total billed: ${formatCurrency(totals.total_billed)}`} color="primary" variant="outlined" />
              <Chip label={`Total paid: ${formatCurrency(totals.total_paid)}`} color="success" variant="outlined" />
              <Chip label={`Total outstanding: ${formatCurrency(totals.total_outstanding)}`} color="warning" variant="outlined" />
              <Chip label={`Total overdue: ${formatCurrency(totals.total_overdue)}`} color="error" variant="outlined" />
              <Chip label={`Revenue (Paid Fees): ${formatCurrency(totals.paid_revenue)}`} color="secondary" variant="outlined" />
              <Chip label={`Revenue (Outstanding Fees): ${formatCurrency(totals.outstanding_revenue)}`} color="warning" variant="outlined" />
              <Chip label={`Revenue (Overdue Fees): ${formatCurrency(totals.overdue_revenue)}`} color="error" variant="outlined" />
            </Stack>
            {chartData.length ? (
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis tickFormatter={(value) => `$${Number(value).toLocaleString()}`} />
                    <RechartsTooltip
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(label) => `Period: ${label}`}
                    />
                    <Legend />
                    <Bar dataKey="billed" name="Billed" fill="#1565c0" />
                    <Bar dataKey="paid" name="Paid" fill="#2e7d32" />
                    <Bar dataKey="outstanding" name="Outstanding" fill="#ed6c02" />
                    <Bar dataKey="overdue" name="Overdue" fill="#d32f2f" />
                    <Bar dataKey="paid_revenue" name="Revenue (Paid Fees)" fill="#7b1fa2" />
                    <Bar dataKey="outstanding_revenue" name="Revenue (Outstanding Fees)" fill="#8d6e63" />
                    <Bar dataKey="overdue_revenue" name="Revenue (Overdue Fees)" fill="#ad1457" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography color="text.secondary">No period data available for current filters.</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box mt={3}>
        <Card>
          <CardHeader
            title="Invoice list"
            subheader={
              selectedInvoiceIds.length
                ? `${selectedInvoiceIds.length} selected`
                : 'Select rows to export specific invoices'
            }
          />
          <Divider />
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mx: 3, mt: 3 }}>
                  {error}
                </Alert>
              )}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={someVisibleSelected}
                          checked={allVisibleSelected}
                          onChange={handleToggleSelectVisible}
                          inputProps={{ 'aria-label': 'select all visible invoices' }}
                        />
                      </TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell sortDirection={sort.field === 'client_sort_name' ? sort.direction : false}>
                        <TableSortLabel
                          active={sort.field === 'client_sort_name'}
                          direction={sort.field === 'client_sort_name' ? sort.direction : 'asc'}
                          onClick={() => handleRequestSort('client_sort_name')}
                        >
                          Client
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Funding</TableCell>
                      <TableCell>Approval</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell sortDirection={sort.field === 'total_amount' ? sort.direction : false}>
                        <TableSortLabel
                          active={sort.field === 'total_amount'}
                          direction={sort.field === 'total_amount' ? sort.direction : 'asc'}
                          onClick={() => handleRequestSort('total_amount')}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Revenue (Paid)</TableCell>
                      <TableCell>Revenue (Outstanding)</TableCell>
                      <TableCell>Payment source</TableCell>
                      <TableCell>Payment method</TableCell>
                      <TableCell>Payment state</TableCell>
                      <TableCell>Payment date</TableCell>
                      <TableCell>Issue date</TableCell>
                      <TableCell>Due date</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={18} align="center" sx={{ py: 5 }}>
                            <Typography color="text.secondary">No invoices found for the selected filters.</Typography>
                          </TableCell>
                        </TableRow>
                    ) : (
                      invoicePeriodGroups.map((group) => {
                        const groupSummary = periodSummaryByKey.get(group.key);
                        return (
                        <Fragment key={group.key}>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell colSpan={18}>
                              <Stack spacing={1}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={2}
                                >
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => togglePeriodCollapse(group.key)}
                                      aria-label={
                                        collapsedPeriods[group.key]
                                          ? 'Expand period invoices'
                                          : 'Collapse period invoices'
                                      }
                                    >
                                      {collapsedPeriods[group.key] ? (
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      ) : (
                                        <KeyboardArrowUpIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                      {getPeriodDisplayLabel(group.periodStart, group.periodEnd)}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={`${group.invoices.length} invoice${
                                        group.invoices.length === 1 ? '' : 's'
                                      }`}
                                      variant="outlined"
                                    />
                                  </Stack>
                                </Stack>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    label={`Billed: ${formatCurrency(groupSummary?.total_billed ?? 0)}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    label={`Paid: ${formatCurrency(groupSummary?.total_paid ?? 0)}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label={`Outstanding: ${formatCurrency(groupSummary?.total_outstanding ?? 0)}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    label={`Overdue: ${formatCurrency(groupSummary?.total_overdue ?? 0)}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    label={`Fees (Paid): ${formatCurrency(groupSummary?.paid_revenue ?? 0)}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    label={`Fees (Outstanding): ${formatCurrency(groupSummary?.outstanding_revenue ?? 0)}`}
                                  />
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    label={`Fees (Overdue): ${formatCurrency(groupSummary?.overdue_revenue ?? 0)}`}
                                  />
                                </Stack>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          {!collapsedPeriods[group.key] &&
                            group.invoices.map((invoice) => renderInvoiceRow(invoice))}
                        </Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalCount}
                page={pagination.page}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </Card>
      </Box>

      <Dialog open={bulkDialogOpen} onClose={closeBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk generate invoices</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Funding segment"
              value={bulkFundingFilter}
              onChange={(event) => setBulkFundingFilter(event.target.value)}
              helperText="Filter clients by ad spend funding."
            >
              <MenuItem value="all">All clients</MenuItem>
              <MenuItem value="guru_funds_spend">Lead Gurus funded ad spend</MenuItem>
              <MenuItem value="client_funds_spend">Client funded ad spend</MenuItem>
            </TextField>
            <TextField
              select
              label="Clients"
              value={selectedClientIds}
              onChange={(event) => setSelectedClientIds(event.target.value.map((value) => Number(value)))}
              SelectProps={{ multiple: true, renderValue: (selected) => `${selected.length} selected` }}
              helperText="Only clients with spend in the selected period are shown."
            >
              {bulkClientsLoading ? (
                <MenuItem disabled>Loading clients…</MenuItem>
              ) : !filteredClients.length ? (
                <MenuItem disabled>No clients with spend found for this period.</MenuItem>
              ) : (
                filteredClients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={client.id} />
                      <Typography variant="body2">
                        {client.name} ({formatCurrency(client.periodSpend)})
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))
              )}
            </TextField>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={handleClearBulkClients}>
                Clear
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAllBulkClients}
                disabled={!filteredClients.length}
              >
                Select all
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                type="date"
                label="Period start"
                value={bulkPeriod.start}
                onChange={(event) => setBulkPeriod((prev) => ({ ...prev, start: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label="Period end"
                value={bulkPeriod.end}
                onChange={(event) => setBulkPeriod((prev) => ({ ...prev, end: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
            <Alert severity="info">
              Leave the period empty to use the default billing window for this cycle.
            </Alert>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
            {bulkSummaryDetails}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDialog}>Cancel</Button>
          <LoadingButton
            variant="contained"
            loading={bulkSubmitting}
            onClick={handleBulkSubmit}
            startIcon={<SummarizeIcon />}
          >
            Generate
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Delete draft invoice?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography variant="body2">
              This will permanently delete{' '}
              <strong>{deleteTarget?.number || (deleteTarget ? `Invoice #${deleteTarget.id}` : 'this draft')}</strong>.
            </Typography>
            <Alert severity="warning">Only draft invoices can be deleted.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={Boolean(deletingId)}>
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={Boolean(deletingId)}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog open={voidDialogOpen} onClose={closeVoidDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Void invoice?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Voiding keeps the invoice for history and reporting, but removes it from active billing workflows.
            </Alert>
            <Typography variant="body2">
              You are voiding <strong>{voidTarget?.number || (voidTarget ? `Invoice #${voidTarget.id}` : 'this invoice')}</strong>.
            </Typography>
            <TextField
              label="Reason"
              value={voidReason}
              onChange={(event) => setVoidReason(event.target.value)}
              multiline
              minRows={3}
              placeholder="Optional reason for voiding this invoice"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVoidDialog} disabled={Boolean(voidingId)}>
            Cancel
          </Button>
          <LoadingButton color="warning" onClick={handleVoid} loading={Boolean(voidingId)}>
            Void invoice
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
